import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Alert, Typography } from "@mui/material";
import { CalendarDate, today, getLocalTimeZone, startOfMonth } from "@internationalized/date";
import { useDispatch, useSelector } from "react-redux";
import { selectCommissionCanViewReport, selectPermissions } from "../../redux/authSlice";
import { ROUTES } from "../../routes/routes.constants";
import { ApiCall } from "../../helper/axios";
import { toast } from "../../common_components/Toast/ToastUtils";
import PageContainer from "../../common_components/PageContainer";
import ClientGroupHeader from "../../common_components/ClientGroupHeader";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CommonLoader from "../../common_components/CommonLoader";
import CustomMonthPicker from "../../common_components/daterangepicker/CustomMonthPicker";
import { Dropdown, ExportButton } from "../../common_components/FormInputs";
import ReportStatCardsV2 from "./components/ReportStatCardsV2";
import PerformanceReportTab from "./components/PerformanceReportTab";
import { fetchCommissionReport, selectCommissionReport, selectCommissionReportLoading, selectCommissionReportError, selectCommissionReportRequest, } from "../../redux/commissionTrackerSlice";
import { mapReportSummary, mapReportRows, mapReportTotals } from "./commissionReport.mappers";
const SESSION_KEY = "commission_tracker_client_id";
const CHANNEL_KEY = "commission_tracker_channel_type";
const MONTH_KEY = "commission_tracker_month";
const CHANNEL_LABELS = { affiliate: "Affiliate", paid_media: "Paid Media" };
const isValidObjectId = (v) => !!v && /^[a-f\d]{24}$/i.test(v);
const toYearMonth = (d) => d ? `${d.year}-${String(d.month).padStart(2, "0")}` : "";
// Restore a persisted "YYYY-MM" back into a CalendarDate (first of the month).
const parseSavedMonth = (val) => {
    if (!val || !/^\d{4}-\d{2}$/.test(val))
        return null;
    const [y, m] = val.split("-").map(Number);
    return new CalendarDate(y, m, 1);
};
// Restore a persisted channel type, falling back to "affiliate" (first A→Z).
const readSavedChannel = () => {
    const v = sessionStorage.getItem(CHANNEL_KEY);
    return v === "paid_media" || v === "affiliate" ? v : "affiliate";
};
export default function CommissionTrackerReport() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    // ── Access + session guard ────────────────────────────────
    // The report (inner) page needs "Full Access". "Limited Access" users can only
    // see the listing, so bounce them back. Also bounce if the client id is missing.
    const canViewReport = useSelector(selectCommissionCanViewReport);
    // permissions are never persisted — they start null on a reload and are filled
    // in once /api/auth/me responds. Gate the access check on this so we don't bounce
    // a Full-Access user out before their permissions have loaded.
    const permissions = useSelector(selectPermissions);
    const permissionsLoaded = permissions !== null;
    const sessionClientId = useRef(sessionStorage.getItem(SESSION_KEY));
    const hasValidClient = isValidObjectId(sessionClientId.current);
    const canOpen = canViewReport && hasValidClient;
    useEffect(() => {
        // No client selected → always bounce back to the listing.
        if (!hasValidClient) {
            navigate(ROUTES.commissionTracker.path, { replace: true });
            return;
        }
        // Only decide on access once permissions have actually loaded; otherwise a
        // reload would redirect before /api/auth/me resolves.
        if (permissionsLoaded && !canViewReport) {
            navigate(ROUTES.commissionTracker.path, { replace: true });
        }
    }, [permissionsLoaded, canViewReport, hasValidClient, navigate]);
    // ── Selection state (restored from sessionStorage so a reload keeps them) ──
    const [selectedStore, setSelectedStore] = useState(sessionClientId.current || "");
    const [channelType, setChannelType] = useState(readSavedChannel());
    const [month, setMonth] = useState(parseSavedMonth(sessionStorage.getItem(MONTH_KEY))
        || parseSavedMonth(location.state?.month ?? null)
        || startOfMonth(today(getLocalTimeZone())));
    // Persist selections so they survive a page reload.
    useEffect(() => {
        if (isValidObjectId(selectedStore))
            sessionStorage.setItem(SESSION_KEY, selectedStore);
    }, [selectedStore]);
    useEffect(() => {
        sessionStorage.setItem(CHANNEL_KEY, channelType);
    }, [channelType]);
    useEffect(() => {
        const ym = toYearMonth(month);
        if (ym)
            sessionStorage.setItem(MONTH_KEY, ym);
    }, [month]);
    // ── API state ─────────────────────────────────────────────
    const report = useSelector(selectCommissionReport);
    const reportRequest = useSelector(selectCommissionReportRequest);
    const loading = useSelector(selectCommissionReportLoading);
    const error = useSelector(selectCommissionReportError);
    const token = useSelector((state) => state.auth?.token);
    const [exporting, setExporting] = useState(false);
    const [savingModifiers, setSavingModifiers] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    // ── Fetch (deduped) ───────────────────────────────────────
    const lastKey = useRef(null);
    const loadReport = useCallback(() => {
        if (!isValidObjectId(selectedStore))
            return;
        const yearMonth = toYearMonth(month);
        if (!yearMonth)
            return;
        const payload = { client_id: selectedStore, year_month: yearMonth, channel_type: channelType };
        const key = JSON.stringify(payload);
        if (lastKey.current === key)
            return;
        lastKey.current = key;
        dispatch(fetchCommissionReport(payload));
    }, [dispatch, selectedStore, channelType, month]);
    useEffect(() => {
        if (canOpen)
            loadReport();
    }, [canOpen, loadReport]);
    // Sync local selection to whatever the backend actually resolved (effective
    // channel / first-valid store). Guarded so it only fires on a real mismatch.
    useEffect(() => {
        if (!report || !reportRequest)
            return;
        const isAnswerToCurrentSelection = reportRequest.client_id === selectedStore &&
            reportRequest.year_month === toYearMonth(month) &&
            reportRequest.channel_type === channelType;
        if (!isAnswerToCurrentSelection)
            return;
        if (report.selected_store_id && report.selected_store_id !== selectedStore) {
            setSelectedStore(report.selected_store_id);
        }
        if (report.channel_type && report.channel_type !== channelType) {
            setChannelType(report.channel_type);
        }
    }, [report, reportRequest]); // eslint-disable-line react-hooks/exhaustive-deps
    // ── Dropdown options (from API) ───────────────────────────
    const storeOptions = useMemo(() => (report?.stores || []).map((s) => ({ value: s.id, label: s.name })), [report]);
    // The client/store dropdown roster must PERSIST even when the current selection
    // (client + month) resolves to no goal — in that case the report comes back with
    // an empty `stores` list (or null on error), which would otherwise blank out the
    // dropdown and strand the user with no way to switch clients. Keep the last
    // non-empty roster and keep showing it.
    const [stableStoreOptions, setStableStoreOptions] = useState([]);
    useEffect(() => {
        if (storeOptions.length)
            setStableStoreOptions(storeOptions);
    }, [storeOptions]);
    const clientOptions = stableStoreOptions.length ? stableStoreOptions : storeOptions;
    const channelOptions = useMemo(() => (report?.client?.channel_types || []).slice().sort().map((c) => ({ value: c, label: CHANNEL_LABELS[c] || c })), [report]);
    // ── Report view models ────────────────────────────────────
    const summary = useMemo(() => mapReportSummary(report?.report_data?.config), [report]);
    const rows = useMemo(() => mapReportRows(report?.report_data?.days), [report]);
    const totals = useMemo(() => mapReportTotals(report?.report_data?.footer), [report]);
    const hasReport = !!report?.report_data && rows.length > 0;
    // ── Export (POST → CSV blob download) ─────────────────────
    const handleExport = useCallback(async () => {
        const yearMonth = toYearMonth(month);
        const storeId = report?.selected_store_id || selectedStore;
        const channel = report?.channel_type || channelType;
        if (exporting || !isValidObjectId(storeId) || !yearMonth || !channel)
            return;
        setExporting(true);
        try {
            const response = await ApiCall("POST", "/api/commission-tracker/export-commission-tracker", { client_id: storeId, year_month: yearMonth, channel_type: channel }, { Authorization: token || "", "x-module-key": "commission_tracker" }, 7 * 60 * 1000, "blob");
            if (!response || response.status !== 200 || !response.data)
                throw new Error("Export failed");
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const namePart = report?.client?.name ? `${report.client.name}_` : "";
            a.download = `commission_tracker_${namePart}${yearMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            console.error("Commission tracker export error:", err);
            toast.error("Failed to export the commission tracker. Please try again.");
        }
        finally {
            setExporting(false);
        }
    }, [exporting, month, selectedStore, channelType, report, token]);
    // ── Save Modifier (POST → recompute) ─────────────────────
    const handleSaveModifiers = useCallback(async (modifiers) => {
        const yearMonth = toYearMonth(month);
        const storeId = report?.selected_store_id || selectedStore;
        const channel = report?.channel_type || channelType;
        if (savingModifiers || !isValidObjectId(storeId) || !yearMonth || !channel)
            return;
        setSavingModifiers(true);
        try {
            const response = await ApiCall("POST", "/api/commission-tracker/save-modifier", { client_id: storeId, year_month: yearMonth, channel_type: channel, modifiers }, { Authorization: token || "", "x-module-key": "commission_tracker" });
            const status = response?.status ?? response?.response?.status;
            const ok = status === 200 && (response?.data?.success ?? true);
            if (!ok) {
                throw new Error(response?.data?.message || response?.response?.data?.message || "Save failed");
            }
            toast.success("Modifiers saved successfully.");
            // Refresh the report so the recomputed values show.
            lastKey.current = null;
            loadReport();
        }
        catch (err) {
            console.error("Save modifiers error:", err);
            toast.error(err?.message || "Failed to save modifiers. Please try again.");
            throw err;
        }
        finally {
            setSavingModifiers(false);
        }
    }, [savingModifiers, month, selectedStore, channelType, report, token, loadReport]);
    // ── Save a single day's edited value / notes (PATCH → recompute) ──────────
    const handleSaveDayEdit = useCallback(async (args) => {
        const yearMonth = toYearMonth(month);
        const storeId = report?.selected_store_id || selectedStore;
        const channel = report?.channel_type || channelType;
        if (!isValidObjectId(storeId) || !yearMonth || !channel)
            return;
        try {
            setSavingModifiers(true);
            const body = { client_id: storeId, year_month: yearMonth, channel_type: channel, date: args.date };
            if (args.updated_value !== undefined)
                body.updated_value = args.updated_value;
            if (args.notes !== undefined)
                body.notes = args.notes;
            const response = await ApiCall("PATCH", "/api/commission-tracker/update-commission", body, { Authorization: token || "", "x-module-key": "commission_tracker" });
            const status = response?.status ?? response?.response?.status;
            const ok = status === 200 && (response?.data?.success ?? true);
            if (!ok)
                throw new Error(response?.data?.message || response?.response?.data?.message || "Save failed");
            // A daily value change affects MTD / Diff / Commission → refresh the report.
            // Notes don't affect the computation, so no refetch needed.
            if (args.updated_value !== undefined || args.notes !== undefined) {
                lastKey.current = null;
                loadReport();
            }
        }
        catch (err) {
            console.error("Save day edit error:", err);
            toast.error(err?.message || "Failed to save. Please try again.");
            throw err;
        }
        finally {
            setSavingModifiers(false);
        }
    }, [month, selectedStore, channelType, report, token, loadReport]);
    const refreshDisabledReason = useMemo(() => report?.invoice_generated
        ? "This client's invoice for this month has already been generated, so its commission data can no longer be refreshed."
        : undefined, [report?.invoice_generated]);
    // ── Refresh client commission (POST → re-sync, then refetch report) ───────
    const handleRefresh = useCallback(async () => {
        const yearMonth = toYearMonth(month);
        const storeId = report?.selected_store_id || selectedStore;
        const channel = report?.channel_type || channelType;
        if (refreshing || !isValidObjectId(storeId) || !yearMonth || !channel)
            return;
        if (report?.invoice_generated)
            return;
        setRefreshing(true);
        try {
            const response = await ApiCall("POST", "/api/commission-tracker/refresh", { client_id: storeId, year_month: yearMonth, channel_type: channel }, { Authorization: token || "", "x-module-key": "commission_tracker" }, 7 * 60 * 1000);
            const status = response?.status ?? response?.response?.status;
            const ok = status === 200 && (response?.data?.success ?? true);
            if (!ok)
                throw new Error(response?.data?.message || response?.response?.data?.message || "Refresh failed");
            toast.success("Commission data refreshed.");
            // Pull the freshly-synced figures + new last-updated timestamp.
            lastKey.current = null;
            loadReport();
        }
        catch (err) {
            console.error("Commission tracker refresh error:", err);
            toast.error(err?.message || "Failed to refresh. Please try again.");
        }
        finally {
            setRefreshing(false);
        }
    }, [refreshing, month, selectedStore, channelType, report, token, loadReport]);
    const renderDropdownValue = (label, value) => (_jsxs("span", { style: { display: "flex", alignItems: "center", gap: "6px" }, children: [_jsx("span", { style: { fontWeight: 500, color: "#101828" }, children: label }), _jsx("span", { style: { color: "#667085" }, children: value })] }));
    // ✅ Loading State
    if (exporting || savingModifiers || refreshing) {
        return (_jsxs(Box, { sx: {
                position: "absolute",
                inset: 0,
                background: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                zIndex: 100,
            }, children: [_jsx(CommonLoader, { className: "table-loader" }), exporting && (_jsx(Typography, { variant: "body1", color: "text.secondary", children: "Preparing your export. This may take a moment. It will download automatically once it's ready" }))] }));
    }
    if (!canOpen)
        return _jsx(PageContainer, { children: _jsx(Box, {}) });
    return (_jsx(PageContainer, { children: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "8px", position: "relative", minHeight: 300 }, children: [_jsx(ClientGroupHeader, { selectedClientId: selectedStore, clientOptions: clientOptions, handleClientChange: (v) => setSelectedStore(Array.isArray(v) ? v[0] : v) }), _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: report?.last_updated ?? null, onRefresh: handleRefresh, loading: refreshing, moduleName: "commission_tracker", disabledReason: refreshDisabledReason }), rightSection: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-end" }, children: [channelOptions.length > 1 &&
                                _jsx(Dropdown, { value: channelType, onChange: (v) => setChannelType(v), options: channelOptions, placeholder: "Type", displayValue: renderDropdownValue("Type", CHANNEL_LABELS[channelType] || "—"), leftIcon: "/assets/TypeC.svg", size: "sm", fullWidth: false, sortByLabel: false, className: "header-dropdown" }), hasReport &&
                                _jsx(ExportButton, { onClick: handleExport }), _jsx(CustomMonthPicker, { value: month, onChange: (v) => v !== "ALL_TIME" && setMonth(v), placeholder: "Select Month", showAllTime: false })] }) }), (!permissionsLoaded || loading) ? (_jsx(Box, { sx: { position: "absolute", inset: 0, top: 140, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : error ? (_jsx(Alert, { severity: "error", sx: { borderRadius: "10px", alignItems: "center" }, children: error })) : !hasReport ? (_jsx(Alert, { severity: "info", sx: { borderRadius: "10px", alignItems: "center" }, children: "No commission report data available for this store, channel, and month." })) : (_jsxs(_Fragment, { children: [_jsx(ReportStatCardsV2, { summary: summary }), _jsx(PerformanceReportTab, { rows: rows, totals: totals, metricWord: summary.metricWord, isOrders: summary.isOrders, onSaveModifiers: handleSaveModifiers, savingModifiers: savingModifiers, onSaveDayEdit: handleSaveDayEdit })] }))] }) }));
}
